/**
 * Utility functions for calculated fields in DDR forms
 */

/**
 * Calculate duration in hours between two time values
 * @param {string} startTime - Start time in HH:mm format
 * @param {string} endTime - End time in HH:mm format
 * @returns {number} Duration in hours (decimal)
 */
export const calculateDurationFromTimes = (startTime, endTime) => {
  console.log('🧮 Duration calculation input:', { startTime, endTime, startType: typeof startTime, endType: typeof endTime });
  
  // Return empty string if either time is missing, empty, or invalid
  if (!startTime || !endTime || startTime === '' || endTime === '' || startTime === '00:00' && endTime === '00:00') {
    console.log('🧮 Missing or invalid time values, returning empty string');
    return '';
  }
  
  try {
    // Handle various time formats
    const parseTime = (timeStr) => {
      if (!timeStr || timeStr === '') return null;
      
      const cleanTime = timeStr.toString().trim();
      console.log('🧮 Parsing time:', cleanTime);
      
      // Handle formats: HH:mm, HH:mm:ss, or just HH
      const parts = cleanTime.split(':');
      if (parts.length === 0) return null;
      
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10) || 0;
      const seconds = parseInt(parts[2], 10) || 0;
      
      // Validate time values
      if (isNaN(hours) || hours < 0 || hours > 23) return null;
      if (minutes < 0 || minutes > 59) return null;
      if (seconds < 0 || seconds > 59) return null;
      
      const totalHours = hours + (minutes / 60) + (seconds / 3600);
      console.log('🧮 Parsed time result:', { hours, minutes, seconds, totalHours });
      return totalHours;
    };

    const startHours = parseTime(startTime);
    const endHours = parseTime(endTime);
    
    console.log('🧮 Parsed hours:', { startHours, endHours });
    
    if (startHours === null || endHours === null) {
      console.log('🧮 Could not parse times, returning empty string');
      return '';
    }
    
    let duration = endHours - startHours;
    console.log('🧮 Initial duration:', duration);
    
    // Handle overnight operations (end time is next day)
    if (duration < 0) {
      duration += 24; // Add 24 hours for next day
      console.log('🧮 Overnight period detected, adjusted duration:', duration);
    }
    
    // Round to 2 decimal places
    const result = Math.round(duration * 100) / 100;
    console.log('🧮 Final duration result:', result);
    
    return result;
  } catch (error) {
    console.warn('🧮 Error calculating duration:', error);
    return '';
  }
};

/**
 * Sum multiple field values
 * @param {...number} values - Values to sum
 * @returns {number} Sum of values
 */
export const sumFields = (...values) => {
  return values.reduce((sum, val) => {
    const num = parseFloat(val) || 0;
    return sum + num;
  }, 0);
};

/**
 * Calculate difference between two values
 * @param {number} value1 - First value
 * @param {number} value2 - Second value
 * @returns {number} Difference (value1 - value2)
 */
export const calculateDifference = (value1, value2) => {
  const num1 = parseFloat(value1) || 0;
  const num2 = parseFloat(value2) || 0;
  return num1 - num2;
};

/**
 * Calculate percentage
 * @param {number} value - Numerator value
 * @param {number} total - Denominator value
 * @returns {number} Percentage (0-100)
 */
export const calculatePercentage = (value, total) => {
  const num = parseFloat(value) || 0;
  const totalNum = parseFloat(total) || 0;
  
  if (totalNum === 0) return 0;
  
  return Math.round((num / totalNum) * 100 * 100) / 100; // Round to 2 decimal places
};

/**
 * Safe JavaScript expression evaluator for calculated fields
 * @param {string} expression - JavaScript expression to evaluate
 * @param {Object} context - Object containing field values as variables
 * @returns {any} Result of the expression evaluation
 */
export const evaluateCalculation = (expression, context) => {
  if (!expression || typeof expression !== 'string') {
    return '';
  }
  
  try {
    // Create a safe context with available fields and helper functions
    const safeContext = {
      // Field values from context
      ...context,
      
      // Helper functions available in expressions
      Math,
      parseFloat,
      parseInt,
      Number,
      String,
      
      // Custom helper functions
      duration: calculateDurationFromTimes,
      sum: sumFields,
      diff: calculateDifference,
      percentage: calculatePercentage,
      
      // Time and date helpers
      timeToHours: (timeStr) => {
        if (!timeStr) return 0;
        const parts = timeStr.toString().split(':');
        const hours = parseInt(parts[0], 10) || 0;
        const minutes = parseInt(parts[1], 10) || 0;
        return hours + (minutes / 60);
      },
      
      hoursToTime: (hours) => {
        if (!hours || isNaN(hours)) return '00:00';
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      },
      
      round: (value, decimals = 2) => {
        const factor = Math.pow(10, decimals);
        return Math.round((parseFloat(value) || 0) * factor) / factor;
      },
      
      abs: Math.abs,
      max: Math.max,
      min: Math.min,
      
      // Conditional helpers
      ifelse: (condition, trueValue, falseValue) => condition ? trueValue : falseValue
    };
    
    // Remove potentially dangerous globals and functions
    const bannedKeywords = [
      'eval', 'Function', 'window', 'document', 'global', 'process', 
      'require', 'import', 'export', 'fetch', 'XMLHttpRequest',
      'setTimeout', 'setInterval', 'clearTimeout', 'clearInterval'
    ];
    
    // Check for banned keywords
    for (const keyword of bannedKeywords) {
      if (expression.includes(keyword)) {
        console.warn(`Calculation expression contains banned keyword: ${keyword}`);
        return '';
      }
    }
    
    // Create function with the safe context
    const contextKeys = Object.keys(safeContext);
    const contextValues = Object.values(safeContext);
    
    // Use Function constructor to evaluate expression safely
    const func = new Function(...contextKeys, `
      "use strict";
      try {
        return (${expression});
      } catch (error) {
        console.warn('Calculation error:', error.message);
        return '';
      }
    `);
    
    const result = func(...contextValues);
    
    // Ensure result is a reasonable value
    if (result === null || result === undefined) {
      return '';
    }
    if (typeof result === 'number' && (isNaN(result) || !isFinite(result))) {
      return '';
    }
    
    return result;
    
  } catch (error) {
    console.warn('Error evaluating calculation expression:', expression, error);
    return '';
  }
};

/**
 * Check if a field has calculation configuration
 * @param {Object} field - Field configuration object
 * @returns {boolean} True if field has calculation
 */
export const isCalculatedField = (field) => {
  const result = !!(field && (field.calculation || field.calculationType));
  
  // Debug: Always log field check for debugging
  console.log('🔍 isCalculatedField check:', {
    fieldName: field?.name,
    dbColumn: field?.dbColumn,
    hasCalculation: !!field?.calculation,
    hasCalculationType: !!field?.calculationType,
    calculation: field?.calculation,
    calculationType: field?.calculationType,
    result: result,
    fullField: field
  });
  
  return result;
};

/**
 * Get dependent fields for a calculated field
 * @param {string} expression - Calculation expression
 * @param {Array} allFields - All fields in the section
 * @returns {Array} Array of field names that this calculation depends on
 */
export const getDependentFields = (expression, allFields) => {
  if (!expression || !allFields) return [];
  
  const dependencies = new Set();
  
  // Extract field references from expression
  // Look for field names that match other fields in the section
  allFields.forEach(field => {
    if (field.dbColumn || field.name) {
      const fieldName = field.dbColumn || field.name;
      // Check if field name appears in expression as a whole word
      const regex = new RegExp(`\\b${fieldName}\\b`, 'g');
      if (regex.test(expression)) {
        dependencies.add(fieldName);
      }
    }
  });
  
  return Array.from(dependencies);
};

/**
 * Calculate all dependent fields when a source field changes
 * @param {string} changedField - Field that was changed
 * @param {Object} formData - Current form data
 * @param {Array} allFields - All fields in the section
 * @param {Function} onFieldChange - Callback to update field values
 * @param {number} rowIndex - Row index for table fields (null for form fields)
 */
export const recalculateFields = (changedField, formData, allFields, onFieldChange, rowIndex = null) => {
  if (!changedField || !formData || !allFields || !onFieldChange) return;
  
  console.log('🧮 Recalculating fields after change:', {
    changedField,
    formData,
    allFields: allFields.length,
    rowIndex
  });
  
  // Find all calculated fields that depend on the changed field
  const calculatedFields = allFields.filter(field => isCalculatedField(field));
  
  calculatedFields.forEach(field => {
    const expression = field.calculation || field.calculationType;
    if (!expression) return;
    
    const dependencies = getDependentFields(expression, allFields);
    
    // If this calculated field depends on the changed field
    if (dependencies.includes(changedField)) {
      console.log('🧮 Recalculating field:', {
        calculatedField: field.dbColumn || field.name,
        expression,
        dependencies,
        changedField
      });
      
      // Create context with current form data
      const context = { ...formData };
      
      // Evaluate the expression
      const calculatedValue = evaluateCalculation(expression, context);
      
      console.log('🧮 Calculated result:', {
        field: field.dbColumn || field.name,
        expression,
        context,
        result: calculatedValue
      });
      
      // Update the field value
      if (calculatedValue !== '' && calculatedValue !== null && calculatedValue !== undefined) {
        const fieldColumn = field.dbColumn || field.name;
        onFieldChange(fieldColumn, calculatedValue, rowIndex);
      }
    }
  });
};

/**
 * Main calculator function that routes to appropriate calculation based on type
 * @param {string} calculationType - Type of calculation
 * @param {Array} sources - Source field values
 * @param {Object} rowData - Complete row data for context
 * @returns {number|string} Calculated value
 */
export const calculateFieldValue = (calculationType, sources = [], rowData = {}) => {
  if (!calculationType || !sources || sources.length === 0) return '';
  
  switch (calculationType) {
    case 'duration_from_times':
      return calculateDurationFromTimes(sources[0], sources[1]);
      
    case 'sum_fields':
      return sumFields(...sources);
      
    case 'difference':
      return calculateDifference(sources[0], sources[1]);
      
    case 'percentage':
      return calculatePercentage(sources[0], sources[1]);
      
    default:
      console.warn(`Unknown calculation type: ${calculationType}`);
      return '';
  }
};

/**
 * Get source field values from row data based on field configuration
 * @param {Object} fieldConfig - Field configuration with calculationSources
 * @param {Object} rowData - Row data object
 * @returns {Array} Array of source values
 */
export const getSourceValues = (fieldConfig, rowData) => {
  if (!fieldConfig.calculationSources || !rowData) return [];
  
  return fieldConfig.calculationSources.map(sourceColumn => {
    if (!sourceColumn) return null;
    return rowData[sourceColumn] || null;
  });
}; 